import { useState } from "react";
import { useTranslation } from "react-i18next";
import { pickLang, type EventTexts } from "@shared";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { currentLang } from "../i18n";
import * as api from "./gameApi";

interface Props {
  texts: EventTexts;
  /** Appelé une fois le message validé côté serveur. */
  onAccepted: () => void;
  /** Relecture depuis l'accueil : le bouton ferme, sans rien réenregistrer. */
  rereading?: boolean;
  onClose?: () => void;
}

/**
 * Mot de bienvenue, affiché à la première connexion.
 *
 * Il reste relisable depuis l'accueil : un jeune qui valide trop vite ne doit
 * pas perdre l'explication pour toujours.
 */
export default function WelcomeScreen({ texts, onAccepted, rereading = false, onClose }: Props) {
  const { t } = useTranslation();
  const lang = currentLang();
  const [busy, setBusy] = useState(false);

  async function accept(): Promise<void> {
    if (rereading) {
      onClose?.();
      return;
    }
    setBusy(true);
    try {
      await api.acceptWelcome();
    } catch {
      // Hors-ligne : on laisse quand même entrer dans le jeu. Le message
      // réapparaîtra à la prochaine connexion, ce qui est sans gravité.
    } finally {
      setBusy(false);
      onAccepted();
    }
  }

  return (
    <main className="screen">
      <div className="card card--welcome">
        <p className="summary__badge">🛸</p>
        <h1 className="welcome__title">{pickLang(texts.welcome_title, lang)}</h1>

        {/* Le texte vient de l'admin : on respecte ses sauts de ligne sans
            interpréter de balises, donc sans risque d'injection. */}
        <p className="welcome__body">{pickLang(texts.welcome_body, lang)}</p>

        <div className="welcome__missions">
          <section className="welcome__mission">
            <h2 className="welcome__mission-title">{pickLang(texts.mission1_title, lang)}</h2>
            <p className="welcome__mission-body">{pickLang(texts.mission1_body, lang)}</p>
          </section>
          <section className="welcome__mission">
            <h2 className="welcome__mission-title">{pickLang(texts.mission2_title, lang)}</h2>
            <p className="welcome__mission-body">{pickLang(texts.mission2_body, lang)}</p>
          </section>
        </div>

        <button
          type="button"
          className="button button--primary button--full button--big"
          disabled={busy}
          onClick={() => void accept()}
        >
          {rereading ? t("welcome.close") : t("welcome.accept")}
        </button>
      </div>

      {/* Le sélecteur manquait ici alors qu'il est présent partout ailleurs :
          un jeune arrivant sur cet écran n'avait aucun moyen de passer à
          l'anglais avant d'avoir validé sa mission. */}
      <footer className="screen__footer screen__footer--centered">
        <LanguageSwitcher />
      </footer>
    </main>
  );
}
